import { Dialog, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { MTableToolbar } from 'material-table';
import moment from 'moment';
import React, { ReactElement } from 'react';

import { ContextualSegmentEventTypes, track } from '~/src/dash/analytics/analytics';
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import {
  deleteContextualSegment,
  getContextualSegmentsByPublisherID,
  IContextualSegment,
  IGeneralResource,
  ThirdPartyComscore,
  ThirdPartyIAS,
  ThirdPartyIris,
  updateContextualSegment,
} from '~/src/dash/api/contextual-segments';
import { ChipInventory } from '~/src/dash/components/channel-list';
import {
  IconMapKey,
  IconMapKeyComscore,
  IconMapKeyIAS,
  IconMapKeyIris,
  IContextualSegmentRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBidderSettingsExisting,
  RouteCampaignExisting,
  RouteContextualSegmentExisting,
  RouteContextualSegmentNew,
  RouteDealEdit,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { ButtonView } from '~/src/dash/views/button-view';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  loading: boolean;
  updating: boolean;
  snackbarMessage: string;
  snackbarError: boolean;
  activeFilter: string;
  contextualSegments: IContextualSegment[];
  // used for modals
  openDealsModal: boolean;
  openHeaderbiddersModal: boolean;
  openLineItemsModal: boolean;
  Deals: IGeneralResource[];
  Headerbidders: IGeneralResource[];
  LineItems: IGeneralResource[];
  lineItemToOrderMap: { [key: string]: string };
}

export const ContextualSegmentSettingsListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      updating: false,
      loading: false,
      snackbarMessage: '',
      snackbarError: false,
      activeFilter: 'Active',
      contextualSegments: [] as IContextualSegment[],
      // used for modals
      openDealsModal: false,
      openHeaderbiddersModal: false,
      openLineItemsModal: false,
      Deals: [] as IGeneralResource[],
      Headerbidders: [] as IGeneralResource[],
      LineItems: [] as IGeneralResource[],
      lineItemToOrderMap: {} as { [key: string]: string },
    };

    componentDidMount() {
      this.trackEvent('Contextual Segment List - View');
      this.getContextualSegments();
    }

    trackEvent = (
      eventTitle: ContextualSegmentEventTypes,
      time_to_load_sec = 0,
      rows_per_page = 0,
      filter = '',
    ): void => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      if (['Contextual Segment List - Contextual Segments List Loaded'].includes(eventTitle)) {
        track(
          eventTitle,
          {
            is_super_user,
            time_to_load_sec,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Contextual Segment List - Change Rows Per Page') {
        track(
          eventTitle,
          {
            is_super_user,
            rows_per_page,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Contextual Segment List - Use Status Filter') {
        track(
          eventTitle,
          {
            is_super_user,
            filter,
          },
          this.props.user.user,
        );
      } else {
        track(
          eventTitle,
          {
            is_super_user,
          },
          this.props.user.user,
        );
      }
    };

    getContextualSegments = () => {
      const startTime = moment().unix();
      getContextualSegmentsByPublisherID(this.props.session.publisher.ID)
        .then((resp) => {
          const contextualSegments = resp[0];
          this.trackEvent(
            'Contextual Segment List - Contextual Segments List Loaded',
            moment().unix() - startTime,
            (contextualSegments || []).length,
          );
          if (contextualSegments) {
            this.setState({
              contextualSegments,
              loading: false,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading contextual segments',
            loading: false,
          });
        });
    };

    getContextualSegmentSettingsRoute = (id: number): string => {
      return getRoutePath(RouteContextualSegmentExisting, extractRouteParams(this.props), {
        contextual: id.toString(),
      });
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // ************************************
    // ****** CRUD RELATED FUNCTIONS ******
    // ************************************

    createRoute = () => {
      this.trackEvent('Contextual Segment List - Create Contextual Segment');
      navigate(RouteContextualSegmentNew, extractRouteParams(this.props));
    };

    updateContextualSegment = (itemID: string, checked: boolean) => {
      if (this.state.updating) {
        return;
      }

      // find the segment to be updated
      const contextualSegments = [...this.state.contextualSegments];
      let selectedIndex = -1;
      for (let i = 0; i < contextualSegments.length; i++) {
        if (contextualSegments[i].ID.toString() === itemID) {
          contextualSegments[i].Active = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
          selectedIndex = i;
          break;
        }
      }

      // create an contextualSegment with the fields that need to be updated as well as the ID
      if (selectedIndex >= 0) {
        const contextualSegment = {
          ID: contextualSegments[selectedIndex].ID,
          Active: contextualSegments[selectedIndex].Active,
        } as IContextualSegment;

        this.setState(
          {
            updating: true,
          },
          () => {
            updateContextualSegment(contextualSegment, this.props.session.publisher.ID)
              .then(() => {
                this.setState({
                  contextualSegments,
                  snackbarMessage: 'Successfully saved contextual segment.',
                  snackbarError: false,
                  updating: false,
                });
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Failed to save contextual segment.',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    deleteContextualSegment = (contextualSegmentID: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteContextualSegment(contextualSegmentID, this.props.session.publisher.ID)
            .then(() => {
              const contextualSegments = this.state.contextualSegments.filter(
                (Route: IContextualSegment) => Route.ID.toString() !== contextualSegmentID,
              );
              this.setState({
                contextualSegments,
                snackbarMessage: 'Successfully deleted contextual segment',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting contextual segment',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // ************************************
    // ******** HANDLER FUNCTIONS *********
    // ************************************
    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.name === 'activeFilter') {
        this.trackEvent('Contextual Segment List - Use Status Filter', 0, 0, target.value);
        this.setState({
          [target.name]: target.value,
          page: 0,
        });
      } else {
        this.setState({ [target.name]: target.value });
      }
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({ updating });
    };

    handleOpenDealsModal = (contextualSegmentID: string) => {
      const { contextualSegments } = this.state;
      const foundContextualSegment = contextualSegments.find(
        (contextualSegment: IContextualSegment) =>
          contextualSegment.ID.toString() === contextualSegmentID,
      );
      if (foundContextualSegment) {
        this.setState({
          openDealsModal: true,
          Deals: foundContextualSegment.Deals,
        });
      }
    };

    handleOpenHeaderbiddersModal = (contextualSegmentID: string) => {
      const { contextualSegments } = this.state;
      const foundContextualSegment = contextualSegments.find(
        (contextualSegment: IContextualSegment) =>
          contextualSegment.ID.toString() === contextualSegmentID,
      );
      if (foundContextualSegment) {
        this.setState({
          openHeaderbiddersModal: true,
          Headerbidders: foundContextualSegment.Headerbidders,
        });
      }
    };

    handleOpenLineItemsModal = (contextualSegmentID: string) => {
      const { contextualSegments } = this.state;
      const foundContextualSegment = contextualSegments.find(
        (contextualSegment: IContextualSegment) =>
          contextualSegment.ID.toString() === contextualSegmentID,
      );
      const lineItemToOrderMap = {} as { [key: string]: string };
      if (foundContextualSegment) {
        for (const lineItem of foundContextualSegment.AdserverCampaigns) {
          lineItemToOrderMap[lineItem.ID.toString()] = lineItem.AdserverOrderID;
        }
        this.setState({
          lineItemToOrderMap,
          openLineItemsModal: true,
          LineItems: foundContextualSegment.AdserverCampaigns,
        });
      }
    };

    // ************************************
    // ********* RENDER FUNCTIONS *********
    // ************************************

    // renderCustomContextualSegmentToolbar renders the route toolbar at the top of the list table
    renderCustomContextualSegmentToolbar = (props: any): ReactElement => {
      const { classes } = this.props;
      const { updating, activeFilter } = this.state;

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
            <Grid item>
              <Grid container spacing={2} alignItems='center'>
                <Grid
                  item
                  onClick={() =>
                    this.trackEvent('Contextual Segment List - Search Contextual Segments')
                  }
                  className='MTableToolbarWrapper'
                >
                  <MTableToolbar {...copyProps} />
                </Grid>
                <Grid item>
                  <Typography style={{ fontSize: 14, paddingLeft: 30 }}>Status</Typography>
                </Grid>
                <Grid item>
                  <Select
                    name='activeFilter'
                    value={activeFilter}
                    onChange={this.handleChange}
                    style={{ fontSize: '14px' }}
                  >
                    <MenuItem value={'Active'}>Active</MenuItem>
                    <MenuItem value={'Inactive'}>Inactive</MenuItem>
                    <MenuItem value={'Show All'}>Show All</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <RowButton
              right={
                <ButtonView
                  onClick={() => {
                    this.trackEvent('Contextual Segment List - Create Contextual Segment');
                    this.createRoute();
                  }}
                  text='Create Contextual Segment'
                  active={updating}
                />
              }
            />
          </Grid>
        </Grid>
      );
    };

    renderContextualSegmentLabel = (row: IContextualSegmentRowData) => {
      return row.Labels && row.Labels.length ? (
        <Grid container direction='row' justify='center' alignItems='center'>
          {row.Labels.map((label) => {
            return (
              <Grid style={{ margin: '5px', display: 'inline-block' }}>
                <ChipInventory label={label.Name} />
              </Grid>
            );
          })}
        </Grid>
      ) : null;
    };

    renderList = (): ReactElement => {
      const { user } = this.props;
      const { contextualSegments, activeFilter, loading, updating } = this.state;

      const listTableColumns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: true },
        { field: 'Name', title: 'Name', renderType: 'namelink', searchable: true },
        { field: 'Icons', title: 'Data Sources', renderType: 'icons', searchable: true },
        {
          field: 'ID',
          title: 'ID',
          renderType: 'normal',
          searchable: true,
          hidden: true,
          sorting: false,
        },
        { field: 'Deals', title: 'Deals', renderType: 'modal' },
        { field: 'Headerbidders', title: 'Headerbidders', renderType: 'modal2' },
        { field: 'LineItems', title: 'Line Items', renderType: 'modal3' },
        {
          field: 'Labels',
          title: 'Labels',
          render: (rowData: IContextualSegmentRowData) =>
            this.renderContextualSegmentLabel(rowData),
          searchable: false,
          maxWidth: '100px',
        },
        { field: 'LabelSearch', title: 'Labels', hidden: true, searchable: true },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      let listTableData: IContextualSegmentRowData[] = contextualSegments.map(
        (contextualSegment: IContextualSegment) => {
          const Icons = [] as IconMapKey[];
          const thirdParties = contextualSegment.ThirdPartyContextualSegments.map(
            (segment) => segment.ThirdParty,
          );
          if (thirdParties.includes(ThirdPartyIAS) && !Icons.includes(IconMapKeyIAS)) {
            Icons.push(IconMapKeyIAS);
          }
          if (thirdParties.includes(ThirdPartyIris) && !Icons.includes(IconMapKeyIris)) {
            Icons.push(IconMapKeyIris);
          }
          if (thirdParties.includes(ThirdPartyComscore) && !Icons.includes(IconMapKeyComscore)) {
            Icons.push(IconMapKeyComscore);
          }

          return {
            Active: contextualSegment.Active === ResourceActiveEnabled,
            ID: contextualSegment.ID.toString(),
            Name: checkUserInfoMasked(user.user)
              ? `demo_map_${contextualSegment.ID}`
              : contextualSegment.Name,
            Deals: contextualSegment.Deals.length,
            Headerbidders: contextualSegment.Headerbidders.length,
            LineItems: contextualSegment.AdserverCampaigns.length,
            Labels: contextualSegment.ContextualSegmentLabels,
            LabelSearch: contextualSegment.ContextualSegmentLabels.map((label) => label.Name).join(
              ',',
            ),
            Icons,
          } as IContextualSegmentRowData;
        },
      );

      listTableData = listTableData.filter((rowData: IContextualSegmentRowData) => {
        if (activeFilter === 'Active') {
          return rowData.Active;
        } else if (activeFilter === 'Inactive') {
          return !rowData.Active;
        } else {
          return true;
        }
      });

      return (
        <Grid container direction='column'>
          <Grid item style={{ width: '100%' }}>
            <ListTable
              title={''}
              columns={listTableColumns}
              data={listTableData}
              isLoading={loading}
              isUpdating={updating}
              renderCustomToolbar={this.renderCustomContextualSegmentToolbar}
              handleSetUpdating={this.handleSetUpdating}
              getItemSettingRoute={this.getContextualSegmentSettingsRoute}
              handleOpenModal={this.handleOpenDealsModal}
              handleOpenModal2={this.handleOpenHeaderbiddersModal}
              handleOpenModal3={this.handleOpenLineItemsModal}
              handleChangeActive={this.updateContextualSegment}
              handleDeleteItem={this.deleteContextualSegment}
              fixedEnabled={true}
              pageSize={10}
              readRoleName={'read_audience_manager'}
              writeRoleName={'write_audience_manager'}
            />
          </Grid>
        </Grid>
      );
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const {
        Deals,
        Headerbidders,
        LineItems,
        openDealsModal,
        openHeaderbiddersModal,
        openLineItemsModal,
        snackbarError,
        snackbarMessage,
      } = this.state;

      const readAuthorization = IS_AUTHORIZED(['read_audience_manager'], user.user);
      const writeAuthorization = IS_AUTHORIZED(['write_audience_manager'], user.user);
      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      const DealsModal = Deals.map((deal: IGeneralResource) => {
        return {
          ID: deal.ID,
          Name: deal.Name,
        };
      });

      const HeaderbiddersModal = Headerbidders.map((hb: IGeneralResource) => {
        return {
          ID: hb.ID,
          Name: hb.Name,
        };
      });

      const LineItemsModal = LineItems.map((lineItem: IGeneralResource) => {
        return {
          ID: lineItem.ID,
          Name: lineItem.Name,
        };
      });

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderList()}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openDealsModal}
              onClose={() => this.setState({ openDealsModal: false })}
            >
              <ModalSelectTable
                title='Deals'
                items={DealsModal}
                onSelectRow={(id: string) => {
                  if (id) {
                    this.setState(
                      {
                        openDealsModal: false,
                      },
                      () => {
                        navigate(RouteDealEdit, extractRouteParams(this.props), {
                          dealID: id,
                        });
                      },
                    );
                  }
                }}
                interactive={true}
                width='400px'
              />
            </Dialog>

            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openHeaderbiddersModal}
              onClose={() => this.setState({ openHeaderbiddersModal: false })}
            >
              <ModalSelectTable
                title='Headerbidders'
                items={HeaderbiddersModal}
                onSelectRow={(id: string) => {
                  if (id) {
                    this.setState(
                      {
                        openHeaderbiddersModal: false,
                      },
                      () => {
                        navigate(RouteBidderSettingsExisting, extractRouteParams(this.props), {
                          bidderID: id,
                        });
                      },
                    );
                  }
                }}
                interactive={true}
                width='400px'
              />
            </Dialog>

            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openLineItemsModal}
              onClose={() => this.setState({ openLineItemsModal: false })}
            >
              <ModalSelectTable
                title='Line Items'
                items={LineItemsModal}
                onSelectRow={(id: string) => {
                  if (id) {
                    this.setState(
                      {
                        openLineItemsModal: false,
                      },
                      () => {
                        navigate(RouteCampaignExisting, extractRouteParams(this.props), {
                          order: this.state.lineItemToOrderMap[id],
                          campaignID: id,
                        });
                      },
                    );
                  }
                }}
                interactive={true}
                width='400px'
              />
            </Dialog>

            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
